import type { CharacterCombatStats } from './characterStats';

/** Maximum permanently unlockable dice slots. */
export const MAX_DICE_SLOTS = 6;

/** New games begin with exactly one unlocked die. */
export const STARTING_UNLOCKED_DICE_SLOTS = 1;

/** Combat baseline: 1 full reroll (Reroll All) of unheld unlocked dice per turn. */
export const REROLL_ALL_PER_TURN = 1;

/** Combat baseline: 2 targeted single-die rerolls per turn (any unlocked die, even held). */
export const REROLL_SINGLE_PER_TURN = 2;

/** Skill-offer rerolls granted at the start of each dungeon run. */
export const SKILL_OFFER_REROLLS_PER_DUNGEON = 2;

/** Maximum level for primary dice skills. */
export const MAX_SKILL_LEVEL = 5;

/** Provisional player max HP for the core-combat prototype. */
export const PROVISIONAL_PLAYER_MAX_HP = 40;

/**
 * Provisional XP thresholds to reach the next temporary level.
 * Index 0 unused; xpToReachLevel[n] = XP needed while at level n to reach n+1.
 * Early levels are intentionally quick. OPEN: final XP curve.
 */
export const TEMP_XP_TO_NEXT_LEVEL: readonly number[] = [
  0,
  10, // 1 → 2
  18, // 2 → 3
  28, // 3 → 4
  40, // 4 → 5
  55, // 5 → 6
  70, // 6 → 7+
];

/** Die face values are standard 1–6. */
export type DieFace = 1 | 2 | 3 | 4 | 5 | 6;

export type StatusId = 'bleed' | 'stun' | 'vulnerable';

export interface StatusInstance {
  id: StatusId;
  intensity: number;
  /** Remaining turns the status lasts (decremented after affected actor's turn). */
  remainingTurns: number;
}

export interface DieState {
  /** Stable slot index 0–5. */
  id: number;
  /** Whether this slot is permanently unlocked and may be rolled. */
  unlocked: boolean;
  /** Current face; undefined until first roll of the turn/encounter. */
  face: DieFace | undefined;
  /** Held dice are skipped by Reroll All. */
  held: boolean;
  /** Assigned primary skill id for this dungeon; null at dungeon start. */
  primarySkillId: string | null;
  /** Skill level 1–5 when assigned; 0 when empty. Locked dice stay 0. */
  primarySkillLevel: number;
}

export interface DiceTrayState {
  /** Always length MAX_DICE_SLOTS; locked slots remain present but unusable. */
  dice: DieState[];
  unlockedCount: number;
}

export interface PermanentProgressPlaceholder {
  /** Validated against shared general-tree talents in later milestones. */
  unlockedDiceSlots: number;
}

/** Encounter turn phase. */
export type EncounterPhase =
  | 'rolling'
  | 'comboSelection'
  | 'levelUp'
  | 'resolving'
  | 'enemyActions'
  | 'won'
  | 'lost';

export type EnemyIntentKind = 'strike' | 'guard' | 'special';

export interface EnemyIntention {
  kind: EnemyIntentKind;
  /** Short readable telegraph, e.g. "Strike 5". */
  label: string;
  /** Expected strike damage; 0 for non-damaging intents. */
  damage: number;
  /** Provisional flat damage reduction while guarding. OPEN: formula. */
  guardAmount: number;
}

export interface EnemyState {
  id: string;
  name: string;
  maxHp: number;
  hp: number;
  /**
   * Accumulated Shield pool (damage absorb) — distinct from equipment shield rating.
   * Used by Shield Breaker / Iron Harvest interactions. Default 0.
   */
  shield: number;
  intention: EnemyIntention;
  /** Template id used to roll the next intention. */
  templateId: string;
  statuses: StatusInstance[];
}

export interface PlayerCombatState {
  name: string;
  maxHp: number;
  hp: number;
  /** Temporary shield absorbed before HP. */
  shield: number;
  statuses: StatusInstance[];
}

export type ComboId =
  | 'baseline'
  | 'one_pair'
  | 'two_pairs'
  | 'three_of_a_kind'
  | 'three_pairs'
  | 'full_house'
  | 'four_of_a_kind'
  | 'double_triples'
  | 'four_plus_two'
  | 'five_of_a_kind'
  | 'perfect_straight'
  | 'six_of_a_kind';

export interface ComboDefinition {
  id: ComboId;
  name: string;
  /** Multiplier applied to provisional base attack. */
  multiplier: number;
  /**
   * Required face-count groups, sorted descending (e.g. full house = [3, 2]).
   * Empty means baseline / no named pattern.
   */
  pattern: number[];
  /** When true, faces must be exactly {1,2,3,4,5,6}. */
  perfectStraight?: boolean;
}

export interface EligibleCombo {
  definition: ComboDefinition;
  /** Die ids that form the pattern (left-to-right / ascending id order). */
  participatingDieIds: number[];
  /** Provisional damage preview for UI. */
  estimatedDamage: number;
}

export type SkillCategory = 'damage' | 'defence' | 'status';

/**
 * Level tables are 1-indexed via array[level-1].
 * Values match VANGUARD_FINAL_SPEC — do not invent.
 */
export type VanguardActiveEffect = {
  type: 'vanguard_active';
  activeId:
    | 'cleave'
    | 'rend'
    | 'bloodlust'
    | 'shield_breaker'
    | 'sunder'
    | 'flurry'
    | 'twin_strike'
    | 'iron_harvest'
    | 'kinetic_cascade';
  primaryDamagePctByLevel?: readonly number[];
  splashPctOfPrimaryByLevel?: readonly number[];
  guaranteedBleedStacksByLevel?: readonly number[];
  healMaxHpPctByLevel?: readonly number[];
  stripEnemyShield?: boolean;
  applyVulnerableStacks?: number;
  extraAttacksByLevel?: readonly number[];
  extraAttackDamagePct?: number;
  offHandDamagePctByLevel?: readonly number[];
  shieldFromDamagePctByLevel?: readonly number[];
  shieldDamagePctByLevel?: readonly number[];
  stunTurns?: number;
};

export type SkillEffect =
  | { type: 'damage'; baseAmount: number; perLevel: number }
  | { type: 'shield'; baseAmount: number; perLevel: number }
  | {
      type: 'status';
      statusId: StatusId;
      baseIntensity: number;
      perLevel: number;
      durationTurns: number;
    }
  | {
      type: 'damage_and_status';
      baseDamage: number;
      damagePerLevel: number;
      statusId: StatusId;
      baseIntensity: number;
      intensityPerLevel: number;
      durationTurns: number;
    }
  | {
      type: 'damage_and_shield';
      baseDamage: number;
      damagePerLevel: number;
      baseShield: number;
      shieldPerLevel: number;
    }
  | VanguardActiveEffect;

export interface SkillEquipmentGate {
  requireOffHandWeapon?: boolean;
  requireDualWield?: boolean;
  requireTwoHand?: boolean;
  /** When set, main-hand weapon family must be one of these. */
  mainHandFamilies?: readonly string[];
}

export interface SkillDefinition {
  id: string;
  name: string;
  description: string;
  classId: 'vanguard';
  kind: 'primary_dice' | 'weapon_skill';
  category: SkillCategory;
  maxLevel: number;
  effect: SkillEffect;
  /** Optional equip-snapshot gate for dungeon offers (M9-A). */
  equipmentGate?: SkillEquipmentGate;
}

export type SkillOfferKind = 'first_primary' | 'standard';

export type OfferCardKind = 'skill' | 'enhancement';

export interface SkillOffer {
  /** Selection id — skill id or enhancement id. */
  offerId: string;
  kind: OfferCardKind;
  /** Skill id (or parent skill id for enhancements). */
  skillId: string;
  enhancementId?: string;
  /** True when accepting upgrades an already-assigned instance of this skill. */
  isUpgrade: boolean;
  /** Current level on the die that owns this skill; 0 if not owned. */
  currentLevel: number;
  /** Level after accepting (upgrade → +1, new → 1). */
  nextLevel: number;
  /** Mutual-exclusion note for enhancement cards. */
  exclusionNote?: string | null;
  displayName?: string;
  displayDescription?: string;
}

export interface TemporaryProgress {
  /** Temporary dungeon level; starts at 1; resets between runs. */
  level: number;
  /** XP toward the next temporary level. */
  xp: number;
  /** Level-ups waiting for skill selection. */
  pendingLevelUps: number;
  /** Skill-offer rerolls remaining this dungeon (not combat dice rerolls). */
  skillOfferRerollsRemaining: number;
  /** True until the first primary skill has been chosen this run. */
  awaitingFirstPrimary: boolean;
  /** Current three-choice offer; null when not leveling up. */
  currentOffer: SkillOffer[] | null;
  offerKind: SkillOfferKind | null;
  /**
   * Enhancement ids chosen this run (mutually exclusive pairs enforced on accept).
   * Reset with TemporaryProgress between dungeons.
   */
  selectedEnhancementIds: string[];
}

export type CombatEvent =
  | {
      type: 'player_attack';
      targetId: string;
      comboId: ComboId;
      comboName: string;
      damage: number;
      targetHpAfter: number;
      /** True when the primary attack rolled a critical hit. */
      isCrit?: boolean;
    }
  | {
      type: 'chained_attack';
      source: 'flurry' | 'twin_strike' | 'attack_again';
      targetId: string;
      damage: number;
      isCrit: boolean;
      targetHpAfter: number;
    }
  | {
      type: 'skill_activated';
      dieId: number;
      skillId: string;
      skillName: string;
      skillLevel: number;
      summary: string;
      damage?: number;
      shieldGained?: number;
      statusApplied?: StatusId;
      targetId?: string;
    }
  | {
      type: 'status_tick';
      enemyId: string;
      statusId: StatusId;
      damage: number;
      targetHpAfter: number;
    }
  | {
      type: 'enemy_defeated';
      enemyId: string;
      xpGained: number;
    }
  | {
      type: 'xp_gained';
      amount: number;
      level: number;
      xp: number;
      pendingLevelUps: number;
    }
  | {
      type: 'level_up';
      newLevel: number;
    }
  | {
      type: 'enemy_attack';
      enemyId: string;
      damage: number;
      playerHpAfter: number;
      blocked: number;
      /** True when dodge negated the hit (GDD). Distinct from block. */
      dodged?: boolean;
    }
  | {
      type: 'enemy_stunned';
      enemyId: string;
    }
  | {
      type: 'victory';
    }
  | {
      type: 'defeat';
    }
  | {
      type: 'turn_started';
      turn: number;
    };

export interface EncounterState {
  turn: number;
  phase: EncounterPhase;
  player: PlayerCombatState;
  enemies: EnemyState[];
  selectedTargetId: string | null;
  tray: DiceTrayState;
  hasRolledThisTurn: boolean;
  rerollAllRemaining: number;
  rerollSingleRemaining: number;
  /** Eligible combos for the current faces; empty before roll. */
  eligibleCombos: EligibleCombo[];
  selectedComboId: ComboId | null;
  /** Temporary dungeon XP / skill offers for this run. */
  progress: TemporaryProgress;
  /**
   * When phase is levelUp, resume here after skill selection
   * (`enemyActions` or `won`).
   */
  phaseAfterLevelUp: EncounterPhase | null;
  /**
   * Permanent active skill ids unlocked for this run's offer pool.
   * Empty → provisional M3 pool fallback (playability).
   */
  unlockedActiveSkillIds: string[];
  /**
   * Snapshot of shared character combat stats for this run.
   * Serialised with the encounter so save/resume keeps talent bonuses.
   */
  combatStats: CharacterCombatStats;
  /**
   * Equipment loadout at dungeon entry — used for weapon-skill / Twin Strike
   * offer gates. Null = unequipped (legacy / tests).
   */
  equipmentSnapshot: import('./equipmentCombat').EquipmentSnapshot | null;
  /**
   * M7-D2 dungeon progression used to scale enemies this encounter.
   * Defaults to level 1 Standard when missing (legacy saves).
   */
  dungeonLevel: number;
  mutation: 'standard' | 'hard' | 'brutal';
  log: string[];
  lastEvents: CombatEvent[];
}
